import EwaybillDTO from "@/dto/order/EwaybillDTO";
import OrderDTO from "@/dto/order/OrderDTO";
import { PagedResult } from "./types";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import OrderItemDTO from "@/dto/order/OrderItemDTO";
import { get, post } from "../http/platformServer";
import LogisticsCompany from "@/enum/LogisticsCompany";
import EwaybillType from "@/enum/EwaybillType";

export interface GetEwaybillGeneratedListParams {
    shopId: number;
    originalId?: string;
    waybillNo?: string;
    startPrintedTime?: string;
    endPrintedTime?: string;
    pageNo?: number;
    pageSize?: number;
    offset?: number;
    limit?: number;
}

export interface EwaybillVo extends EwaybillDTO {
    orderIds: number[];

    printedTime?: string;

    /** -------前端值------- */
    shopId: number;
    isSelected: boolean;
    isShowDetail: boolean;
    /** 该运单号关联的包裹信息(部分订单信息也在内) */
    packageInfo: PackageInfo;

    /**
     * ❗❗由packageInfo转化而来的类似订单结构
     * ❗❗无id，合并状态
     *  */
    order: OrderDTO;
    /** ------------------ */
}

/** 查看已生成电子面单列表 */
export function getEwaybillGeneratedList(params: GetEwaybillGeneratedListParams) {
    return post<PagedResult<EwaybillVo>>(params.shopId, "/ewaybill/snapshot/list", params);
}

/** 打印底单相关 @see https://qw15p3s8hv.feishu.cn/wiki/HvJMwl5M2iWHQPk5zUYc8NpDnS6 */
export interface OrderRelation {
    orderId: number;
    originalId: string;
    mergeSplitStatus: OrderMergeSplitStatus;
    splitParentOrderId: number;
    mergeChildOrderIds: number[];
}

//该运单号包裹对应的订单信息，字段名与Order保持一致
interface PackageOrderInfo
    extends Pick<
        OrderDTO,
        | "shopId"
        | "originalId"
        | "orderTime"
        | "paymentTime"
        | "shippingTime"
        | "shippingDeadline"
        | "amounts"
        | "payments"
        | "codAmounts"
        | "discounts"
        | "platformDiscounts"
        | "adjustAmounts"
        | "shippingFee"
        | "weight"
        | "buyerUsername"
        | "buyerNickname"
        | "sequenceNo"
        | "markId"
        | "invoiceInfo"
        | "buyerRemarks"
        | "sellerRemarks"
        | "consignee"
    > {}

//打印时该运单号对应包裹的商品项信息，字段名与OrderItem一致
//用Ewaybill中的orderItemsInfo去构建,orderItem数量也是用orderItemsInfo中保存的数量
//如果Ewaybill.orderItemsInfo没值表示订单所有的OrderItems
export interface PackageOrderItemInfo
    extends Pick<
        OrderItemDTO,
        | "originalId"
        | "simpleTitle"
        | "stockNo"
        | "market"
        | "wholesaler"
        | "weight"
        | "title"
        | "productOriginalId"
        | "productExternalId"
        | "skuOriginalId"
        | "skuExternalId"
        | "skuProperties"
        | "localSkuProperties"
        | "price"
        | "quantity"
        | "amounts"
        | "discounts"
        | "adjustments"
        | "shippingTime"
    > {
    //所属订单的originalId
    orderOriginalId: string;
}
export interface PackageInfo {
    ewayBillId: number;

    /** 该运单号关联的订单 前端需用订单相关信息记录重打日志、回收运单号、 */
    orderRelations: OrderRelation[];

    /** 该运单号包裹对应的订单信息，用于自定义区域的打印，字段名与Order保持一致 */
    orderInfo: PackageOrderInfo;
    orderItemInfos: PackageOrderItemInfo[];
}

export function getEwaybillPackageInfo(shopId: number, ewaybillIds: number[]) {
    return post<PackageInfo[]>(shopId, "/ewaybill/snapshot/package_info/list", { shopId, ewaybillIds });
}

export interface ReprintLogDto {
    ewaybillId: number;
    templateName: string;
    logisticsCompany: LogisticsCompany;
    waybillNos: string;
}

/** 记录日志 */
export function logReprintEwaybillSnapshot(shopId: number, reprintLogDtos: ReprintLogDto[]) {
    return post(shopId, "/ewaybill/snapshot/log_reprint", { shopId, reprintLogDtos });
}

interface EwaybillAccountDto {
    id: number;
    shopId: number;
    type: EwaybillType;
    platformUsername: string;
    ewaybillShippingAddressIds: number[];
}
interface EwaybillDto {
    logisticsCompany: LogisticsCompany;
    waybillNo: string;
    parentWaybillNo: string;
    id: number;
    ewaybillAccount: EwaybillAccountDto;
}

export interface EwaybillsCancelByIdParam {
    shopId: number;
    ewaybills: EwaybillDto[];
}

export interface EwaybillCancelResult {
    orderId: number;
    newLogisticsCompany: LogisticsCompany;
    newWaybillNo: string;
    newHasEwaybillNo: boolean;
    canceledEwaybillIds: number[];
    errorMap: Record<string, string>;
}

export interface EwaybillsCancelProcess {
    taskId: string;
    status: "RUNNING" | "COMPLETED" | "ERROR";
    ewaybillCancelResults: EwaybillCancelResult[];
    errorMsg: string;
}

/** 提交回收运单号任务 */
export function submitCancelEwaybillTask(params: EwaybillsCancelByIdParam, config?) {
    return post<EwaybillsCancelProcess>(
        params.shopId,
        "/ewaybill/snapshot/submit_cancel_ewaybill_task",
        params,
        config,
    );
}

/** 获取回收单号进度 */
export function getCancelEwaybillProcess(shopId: number, taskId: string, config?) {
    return get<EwaybillsCancelProcess>(shopId, "/ewaybill/snapshot/cancel_ewaybill_process", { taskId }, config);
}
